// =============================================================================
// BP-08-05: Vendor Portal Frontend - OrderTable Component
// =============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { Eye, Package, Truck, CheckCircle, XCircle } from 'lucide-react';
import type { VendorOrder, OrderStatus } from '@/features/vendor/types/vendorTypes';
import { OrderStatusBadge } from './OrderStatusBadge';

interface OrderTableProps {
  orders: VendorOrder[];
  onConfirm?: (orderId: string) => void;
  onShip?: (orderId: string) => void;
  isLoading?: boolean;
}

export function OrderTable({ orders, onConfirm, onShip, isLoading }: OrderTableProps) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="animate-pulse">
          <div className="h-12 bg-gray-100 border-b border-gray-200" />
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-20 border-b border-gray-100 flex items-center px-6 gap-4">
              <div className="h-4 bg-gray-200 rounded w-24" />
              <div className="h-4 bg-gray-200 rounded w-32 flex-1" />
              <div className="h-4 bg-gray-200 rounded w-20" />
              <div className="h-4 bg-gray-200 rounded w-20" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-navy mb-2">No Orders Found</h3>
        <p className="text-warm-gray">Orders will appear here once customers place them.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-6 py-3 text-xs font-semibold text-warm-gray uppercase tracking-wide">
                Order
              </th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-warm-gray uppercase tracking-wide">
                Customer
              </th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-warm-gray uppercase tracking-wide">
                Product
              </th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-warm-gray uppercase tracking-wide">
                Date
              </th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-warm-gray uppercase tracking-wide">
                Total
              </th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-warm-gray uppercase tracking-wide">
                Status
              </th>
              <th className="text-right px-6 py-3 text-xs font-semibold text-warm-gray uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {orders.map((order) => (
              <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                {/* Order Number */}
                <td className="px-6 py-4">
                  <Link
                    to={`/vendor/orders/${order.id}`}
                    className="font-mono text-sm font-medium text-terracotta hover:underline"
                  >
                    {order.orderNumber}
                  </Link>
                </td>

                {/* Customer */}
                <td className="px-6 py-4">
                  <div>
                    <p className="font-medium text-navy">{order.customer.contactName}</p>
                    <p className="text-sm text-warm-gray">{order.customer.email}</p>
                  </div>
                </td>

                {/* Product */}
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {order.productImage ? (
                      <img
                        src={order.productImage}
                        alt={order.productName}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                        <Package className="w-5 h-5 text-gray-400" />
                      </div>
                    )}
                    <div className="max-w-[200px]">
                      <p className="font-medium text-navy truncate">{order.productName}</p>
                      <p className="text-sm text-warm-gray">Qty: {order.quantity}</p>
                    </div>
                  </div>
                </td>

                {/* Date */}
                <td className="px-6 py-4 text-sm text-navy">
                  {formatDate(order.orderedAt)}
                </td>

                {/* Total */}
                <td className="px-6 py-4 font-semibold text-navy">
                  {formatCurrency(order.total)}
                </td>

                {/* Status */}
                <td className="px-6 py-4">
                  <OrderStatusBadge status={order.status} />
                </td>

                {/* Actions */}
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    {order.status === 'pending' && onConfirm && (
                      <button
                        onClick={() => onConfirm(order.id)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Confirm Order"
                      >
                        <CheckCircle className="w-5 h-5" />
                      </button>
                    )}
                    {(order.status === 'confirmed' || order.status === 'processing') && onShip && (
                      <button
                        onClick={() => onShip(order.id)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Mark as Shipped"
                      >
                        <Truck className="w-5 h-5" />
                      </button>
                    )}
                    <Link
                      to={`/vendor/orders/${order.id}`}
                      className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-5 h-5" />
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default OrderTable;

